import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { UrlTree } from '@angular/router';
import { sanitizeReturnUrl } from '@core/utils/url-sanitizer';
import { Role } from 'portal-shared';

@Injectable({
  providedIn: 'root',
})
export class RoleRedirectService {
  private readonly router = inject(Router);

  /**
   * Returns true for admin-privileged roles
   */
  isAdminRole(role: Role): boolean {
    return role === Role.ADMIN;
  }

  /**
   * Returns true for roles that should be denied access
   */
  isDeniedRole(role: Role): boolean {
    return role === Role.NONE;
  }

  /**
   * Redirect to the appropriate interface based on user role
   * Delegates to getRedirectUrlTree for consistent logic
   */
  redirectToRoleDefault(role: Role): void {
    const urlTree = this.getRedirectUrlTree(role);
    this.router.navigateByUrl(urlTree);
  }

  private createAccessDeniedUrlTree(
    reason: string,
    returnUrl?: string,
  ): UrlTree {
    const safeReturnUrl = sanitizeReturnUrl(returnUrl);
    const baseParams: Record<string, string> = {
      message: 'access-denied',
      reason,
    };
    return this.router.createUrlTree(['/login'], {
      queryParams: safeReturnUrl
        ? { ...baseParams, returnUrl: safeReturnUrl }
        : baseParams,
    });
  }

  /**
   * Get the appropriate redirect UrlTree for a role without side effects
   * Preferred for guards to handle navigation themselves
   */
  getRedirectUrlTree(role: Role, returnUrl?: string): UrlTree {
    switch (role) {
      case Role.ADMIN:
        return this.router.createUrlTree(['/admin']);
      case Role.VIEWER:
        return this.router.createUrlTree(['/vendors']);
      case Role.NONE:
        return this.createAccessDeniedUrlTree('no-access', returnUrl);
      default:
        return this.createAccessDeniedUrlTree('unknown-role', returnUrl);
    }
  }

  /**
   * Handle role-based access control (side-effect-free)
   * Returns true if access should be denied, false if user should be redirected
   */
  handleRoleAccess(role: Role): boolean {
    // Check if role should be denied access
    return role === Role.NONE;
  }
}
